/**
 * dateManager.ts — Single Source of Truth for all date logic.
 *
 * ─── RULES ────────────────────────────────────────────────────────────────────
 *  1. ALL date formatting, parsing, normalization, and calendar math must go
 *     through this module. Zero date logic should exist in components.
 *  2. Storage / Map keys are always YYYY-MM-DD strings (local time).
 *  3. Never call `new Date(isoString)` for display — it parses as UTC and
 *     will show the wrong day for users west of UTC at midnight.
 *  4. Never call `.toISOString()` to derive "today" — same UTC shift problem.
 *  5. String comparison (`<`, `>`, `===`) is safe on YYYY-MM-DD strings because
 *     zero-padding guarantees lexicographic ≡ chronological order.
 *
 * ─── NO EXTERNAL DEPENDENCIES ─────────────────────────────────────────────────
 *  Uses only the native `Date` API and `Intl.DateTimeFormat`.
 *  dayjs is not required — the native approach is equally safe and avoids an
 *  extra dependency + bundle cost.
 * ──────────────────────────────────────────────────────────────────────────────
 */

// ─────────────────────────────────────────────────────────────────────────────
// STORAGE FORMAT CONSTANT
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The canonical storage format for all dates in this application.
 * All Map keys, Supabase parameters, and state values use this format.
 */
export const STORAGE_FORMAT = 'YYYY-MM-DD' as const;

// ─────────────────────────────────────────────────────────────────────────────
// CORE: normalize
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalizes any date value to a `YYYY-MM-DD` string using the user's LOCAL
 * timezone. Returns `null` for any input that cannot be interpreted as a valid
 * calendar date.
 *
 * Handles:
 *   ✓ `YYYY-MM-DD`          — already canonical, re-validated and re-serialized
 *   ✓ `YYYY-M-D`            — unpadded ("2026-4-1") → "2026-04-01"
 *   ✓ ISO 8601 with time    — "2026-04-01T00:00:00Z" → strips time before parsing
 *                             (avoids UTC→local shift corrupting the date portion)
 *   ✓ Native `Date` objects — extracted via local-time getters, not .toISOString()
 *   ✓ PostgreSQL timestamps  — "2026-04-01 00:00:00+00" → strips after the space
 *   ✗ null / undefined / ""  → null
 *   ✗ Non-date strings       → null
 *   ✗ NaN Dates              → null
 *
 * @example
 *   normalize("2026-04-01T00:00:00Z") // "2026-04-01"
 *   normalize("2026-4-1")             // "2026-04-01"
 *   normalize(new Date())             // "2026-04-01" (today, local time)
 *   normalize(null)                   // null
 *   normalize("not a date")           // null
 */
export function normalize(date: string | Date | unknown): string | null {
  if (date == null) return null;

  // ── Native Date ────────────────────────────────────────────────────────────
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : _fromLocalDate(date);
  }

  // ── String ─────────────────────────────────────────────────────────────────
  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (!trimmed) return null;

    // Strip everything after the first 'T' or space (time component).
    // This is the critical step: we extract the date portion as the AUTHOR
    // intended it, before any timezone conversion can shift it.
    const datePart = trimmed.split(/[T\s]/)[0].replace(/\//g, '-');

    const segments = datePart.split('-');
    if (segments.length !== 3) return null;

    const [y, m, d] = segments.map(Number);
    if (!_isValidYmd(y, m, d)) return null;

    return _pad(y, 4) + '-' + _pad(m, 2) + '-' + _pad(d, 2);
  }

  // ── Number (Unix ms timestamp) ─────────────────────────────────────────────
  if (typeof date === 'number' && Number.isFinite(date)) {
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : _fromLocalDate(d);
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// TODAY
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns today's date as a `YYYY-MM-DD` string in the user's LOCAL timezone.
 *
 * ⚠️  Do not use `new Date().toISOString().slice(0, 10)` — that returns UTC
 *     and will be wrong by one day for users east or west of UTC near midnight.
 */
export const today = (): Date => new Date();
export function getToday(): string {
  return _fromLocalDate(new Date());
}

// ─────────────────────────────────────────────────────────────────────────────
// DATE OBJECT CONVERSION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Converts a native `Date` to a `YYYY-MM-DD` string using local time getters.
 * Unlike `normalize()`, this is typed to only accept `Date` objects and
 * guarantees a non-null return (throws on invalid Date).
 *
 * Use this when you have a `Date` you just constructed and need a storage key.
 */
export function formatDate(d: Date): string {
  if (isNaN(d.getTime())) {
    throw new TypeError('dateManager.formatDate: received an invalid Date');
  }
  return _fromLocalDate(d);
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPARISON HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns `true` if two date values represent the same calendar day.
 * Both inputs are normalized first — safe to call with any supported format.
 * Returns `false` if either input is invalid.
 */
export function isSameDay(
  d1: string | Date | unknown,
  d2: string | Date | unknown,
): boolean {
  const a = normalize(d1);
  const b = normalize(d2);
  return a !== null && b !== null && a === b;
}

/**
 * Returns `true` if the given `YYYY-MM-DD` string is strictly in the future
 * relative to today in local time.
 */
export function isFuture(dateStr: string): boolean {
  return dateStr > getToday();
}

/**
 * Returns `true` if the given `YYYY-MM-DD` string is today or in the past.
 */
export function isPastOrToday(dateStr: string): boolean {
  return dateStr <= getToday();
}

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY FORMATTING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Formats a date for human-readable display using `Intl.DateTimeFormat`.
 *
 * Input accepts any format supported by `normalize()`.
 * The date is constructed in LOCAL time so there is no UTC-shift risk.
 *
 * @example
 *   formatDisplay("2026-04-01")                      // "April 1, 2026"
 *   formatDisplay("2026-04-01", { month: "long" })   // "April"
 *   formatDisplay("2026-04-01", { month: "short", day: "numeric" }) // "Apr 1"
 */
export function formatDisplay(
  date: string | Date | unknown,
  options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day:   'numeric',
    year:  'numeric',
  },
): string {
  const ymd = normalize(date);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', options);
}

/**
 * Formats a date as a short month + day: "Apr 1"
 */
export function formatShort(date: string | Date | unknown): string {
  return formatDisplay(date, { month: 'short', day: 'numeric' });
}

/**
 * Formats a week range for calendar header labels.
 * Example: "Mar 30 – Apr 5, 2026"
 */
export function formatWeekRange(start: Date, end: Date): string {
  const s = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const e = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} – ${e}`;
}

/**
 * Formats a month + optional year label for calendar headers.
 * Example: formatMonthYear(date) → "April 2026"
 *          formatMonthYear(date, "FY 2027") → "April FY 2027"
 */
export function formatMonthYear(date: Date, yearLabel?: string): string {
  const month = date.toLocaleDateString('en-US', { month: 'long' });
  return `${month} ${yearLabel ?? date.getFullYear()}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// RANGE GENERATION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates an inclusive array of `YYYY-MM-DD` strings from `start` to `end`.
 *
 * Both bounds are normalized first. Returns `[]` if either bound is invalid
 * or if `start > end`. Iteration walks in LOCAL time so DST transitions
 * cannot skip a day.
 *
 * @example
 *   getRange('2026-04-01', '2026-04-03')
 *   // ['2026-04-01', '2026-04-02', '2026-04-03']
 */
export function getRange(
  start: string | Date | unknown,
  end:   string | Date | unknown,
): string[] {
  const s = normalize(start);
  const e = normalize(end);
  if (!s || !e || s > e) return [];

  const [sy, sm, sd] = s.split('-').map(Number);
  const cursor = new Date(sy, sm - 1, sd); // local midnight
  const result: string[] = [];

  while (true) {
    const cur = _fromLocalDate(cursor);
    if (cur > e) break;
    result.push(cur);
    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// CALENDAR GRID HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the 0-based day-of-week (0 = Sunday … 6 = Saturday) for a
 * `YYYY-MM-DD` string, using LOCAL time — no UTC shift risk.
 */
export function getDayOfWeek(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

/**
 * Returns the 7 `Date` objects (Sun → Sat) of the week containing `date`,
 * all set to local midnight (00:00:00.000).
 */
export function getWeekDates(date: Date): Date[] {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay()); // rewind to Sunday
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/**
 * Returns `{ firstDay, daysInMonth }` for a given year and 0-based month.
 *   `firstDay`    — 0-based day-of-week the month starts on (0 = Sunday)
 *   `daysInMonth` — total days in that month (28–31)
 */
export function getMonthDays(
  year:  number,
  month: number, // 0-based
): { firstDay: number; daysInMonth: number } {
  return {
    firstDay:    new Date(year, month, 1).getDay(),
    daysInMonth: new Date(year, month + 1, 0).getDate(),
  };
}

/**
 * Builds a flat array of day numbers (1–31) padded with `null` for the
 * calendar grid. Length is always a multiple of 7. `null` = empty cell.
 *
 * @example
 *   buildMonthCells(2026, 3) // April 2026 — starts Wednesday (3)
 *   // [null, null, null, 1, 2, 3, 4, 5, …, 30, null, null]
 */
export function buildMonthCells(year: number, month: number): (number | null)[] {
  const { firstDay, daysInMonth } = getMonthDays(year, month);
  const cells: (number | null)[] = [
    ...Array<null>(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/**
 * Builds a canonical `YYYY-MM-DD` string from a year, 0-based month, and day.
 *
 * This is the **single** place where calendar grid cells produce date keys —
 * guaranteeing every key in every Map, Set, and array comparison is identical.
 */
export function buildDateStr(year: number, month: number, day: number): string {
  return _pad(year, 4) + '-' + _pad(month + 1, 2) + '-' + _pad(day, 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// ISO 8601 WEEK HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the ISO 8601 week number and year for a given `Date`.
 *
 * The ISO week belongs to the year that contains its **Thursday**, so the
 * first/last week of a year may belong to the adjacent calendar year.
 * Returns `{ week, year }` to handle these boundary cases correctly.
 *
 * Compliant with ISO 8601 / RFC 3986. Local-time safe.
 */
export function getISOWeek(d: Date): { week: number; year: number } {
  // Work on a local-midnight copy to isolate from DST edge cases
  const date   = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  // Shift to this week's Thursday (Mon=0 … Sun=6 in ISO land)
  const isoDay = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - isoDay + 3);
  const thursday = new Date(date);
  // First Thursday of the year → anchor for week 1
  const jan4    = new Date(thursday.getFullYear(), 0, 4);
  const jan4Day = (jan4.getDay() + 6) % 7;
  const weekOneMonday = new Date(jan4);
  weekOneMonday.setDate(jan4.getDate() - jan4Day);
  const week = Math.round(
    (thursday.getTime() - weekOneMonday.getTime()) / (7 * 24 * 60 * 60 * 1000),
  ) + 1;
  return { week, year: thursday.getFullYear() };
}

/**
 * Returns the ISO week label string for a `Date`.
 * @example isoWeekLabel(new Date('2026-04-01')) // "W14"
 */
export function isoWeekLabel(d: Date): string {
  return `W${getISOWeek(d).week}`;
}

/**
 * Returns the ISO week label string for a `YYYY-MM-DD` string.
 * @example isoWeekLabelFromStr("2026-04-01") // "W14"
 */
export function isoWeekLabelFromStr(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  return isoWeekLabel(new Date(y, m - 1, d));
}

/**
 * Returns the numeric ISO week number for today in local time.
 * Used in analytics card titles ("Office Goal – Week W14 Only").
 */
export function todayISOWeekNum(): number {
  return getISOWeek(new Date()).week;
}

/**
 * Returns a compact integer key `YYYYWW` for grouping dates by ISO week.
 * Safe to use as a `Map` key and for numeric sorting.
 * @example getWeekKey("2026-04-01") // 202614
 */
export function getWeekKey(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  const { week, year } = getISOWeek(new Date(y, m - 1, d));
  return year * 100 + week;
}

// ─────────────────────────────────────────────────────────────────────────────
// PRIVATE HELPERS  (not exported — internal to this module only)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extracts `YYYY-MM-DD` from a `Date` using LOCAL time components.
 * This is the canonical "Date → string" operation for this entire app.
 */
function _fromLocalDate(d: Date): string {
  return (
    _pad(d.getFullYear(), 4) + '-' +
    _pad(d.getMonth() + 1,  2) + '-' +
    _pad(d.getDate(),        2)
  );
}

/** Zero-pads a number to the given string length. */
function _pad(n: number, len: number): string {
  return String(n).padStart(len, '0');
}

/**
 * Returns `true` if year/month/day form a real calendar date.
 * Month is 1-based here. Handles leap years correctly via native Date.
 */
function _isValidYmd(y: number, m: number, d: number): boolean {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  if (y < 1 || m < 1 || m > 12 || d < 1) return false;
  // new Date(y, m, 0) gives the last day of month m (1-based), so use m here
  const daysInMonth = new Date(y, m, 0).getDate();
  return d <= daysInMonth;
}

// ─────────────────────────────────────────────────────────────────────────────
// NATIVE UI FORMATTING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parses a canonical `YYYY-MM-DD` string safely into a JS `Date` object at local midnight.
 * This guarantees the date stays in the intended local day and avoids the UTC shifting
 * anomaly inherent to `new Date("YYYY-MM-DD")`.
 */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Adds a number of local calendar days to a `YYYY-MM-DD` string. Uses
 * a noon anchor so DST transitions cannot shift the calendar day.
 */
export function addDaysToDateStr(dateStr: string, deltaDays: number): string {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + deltaDays);
  return _fromLocalDate(d);
}

/**
 * Formats a JS `Date` object using the browser's native locale settings (`navigator.language`).
 * This provides localized representations (e.g. "5 de mayo" or "May 5") rather than hardcoded 'en-US'.
 */
export function formatUIDate(
  date: Date, 
  options: Intl.DateTimeFormatOptions = { weekday: 'long', month: 'short', day: 'numeric' }
): string {
  // Graceful fallback if navigator is somehow undefined in a specific runtime
  const locale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
  return new Intl.DateTimeFormat(locale, options).format(date);
}